// VULN-04: Refactored to use centralized ymsAdminClient instead of raw axios.
// This ensures all requests benefit from retry interceptors and consistent error handling.

import axios from 'axios';

import { Endpoints } from '@/constants';
import { store } from '@/store/store';
import { PlantInfo } from '@/types';
import { getDeviceInfo } from '@/utils/deviceInfo';

import { ymsAdminClient } from './taskService';
import {
  SendOTPResponse,
  VerifyOTPResponse,
  ConfigurationResponse,
  UserLogRequest,
  UserLogResponse,
  RefreshTokenResponse,
  UpdateTaskResponse,
  UpdateTaskFinalResponse,
} from './types/authenticatetypes';
import {
  CarrierResponseDTO,
  SignUpRequestDTO,
  SignUpResponseDTO,
  UpdateUserProfileRequestDTO,
  UpdateUserProfileResponseDTO,
} from './types/usertypes';

export async function getConfigurationAsync(): Promise<ConfigurationResponse> {
  const deviceInfo = await getDeviceInfo();
  // eslint-disable-next-line no-console
  console.log('params', {
    device_make: deviceInfo.deviceMake,
    device_model: deviceInfo.deviceModel,
    device_os: deviceInfo.deviceOS,
    device_os_version: deviceInfo.deviceOSVersion,
    device_build_version: deviceInfo.deviceBuildVersion,
    current_app_version: deviceInfo.currentAppVersion,
    app_center_id: deviceInfo.appCenterId,
    device_latitude: deviceInfo.deviceLatitude,
    device_longitude: deviceInfo.deviceLongitude,
  });
  const { data: config } = await ymsAdminClient.post<ConfigurationResponse>(Endpoints.ConfigAPI, {
    device_make: deviceInfo.deviceMake,
    device_model: deviceInfo.deviceModel,
    device_os: deviceInfo.deviceOS,
    device_os_version: deviceInfo.deviceOSVersion,
    device_build_version: deviceInfo.deviceBuildVersion,
    current_app_version: deviceInfo.currentAppVersion,
    app_center_id: deviceInfo.appCenterId,
    device_latitude: deviceInfo.deviceLatitude,
    device_longitude: deviceInfo.deviceLongitude,
  });

  return config;
}

export async function getOTPAsync(countryCode: string, mobile: string) {
  const { data: response } = await ymsAdminClient.post<SendOTPResponse>(Endpoints.Login, {
    country_code: countryCode,
    mobile_no: mobile,
  });

  if (!response.status) {
    throw new Error(response.message);
  }

  return {
    message: response.message,
    registered: response.registered,
  };
}

export async function verifyOTPAsync(
  countryCode: string,
  mobile: string,
  otp: string,
  latitude: number,
  longitude: number,
  deviceId: string
) {
  // TODO: SECURITY — VULN-10: The OTP value is sent in the request body which is
  // visible in the network tab. This is acceptable over HTTPS, but the server
  // MUST NOT log OTP values in plain text in any server-side logs or telemetry.
  const response = await ymsAdminClient.post<VerifyOTPResponse>(Endpoints.VerifyOtp, {
    country_code: countryCode,
    mobile_no: mobile,
    otp,
    lat: latitude,
    lng: longitude,
    device_id: deviceId,
  });

  const accessToken = String(response.headers['access-token'] ?? '');
  if (!response.data.status) {
    throw new Error(response.data.message || 'OTP verification failed');
  }

  if (!response.data.user) {
    throw new Error('OTP is incorrect.');
  }

  return { result: response.data, accessToken };
}

export async function getCarrierList() {
  const { data: response } = await ymsAdminClient.get<CarrierResponseDTO>(Endpoints.CarrierAPI);
  return response;
}

export async function getPlantList() {
  const { data: response } = await ymsAdminClient.get<PlantInfo[]>(Endpoints.plantsAPI);
  return response;
}

// ─────────────────────────────────────────────────────────────────
// Sign Up API
// ─────────────────────────────────────────────────────────────────

export async function signUp(request: SignUpRequestDTO): Promise<SignUpResponseDTO> {
  try {
    const response = await ymsAdminClient.post<SignUpResponseDTO>(Endpoints.SignUpAPI, request);
    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.data) {
      const data = error.response.data as Record<string, unknown>;
      return {
        status: typeof data['status'] === 'boolean' ? data['status'] : false,
        registered: typeof data['registered'] === 'boolean' ? data['registered'] : false,
        message: typeof data['message'] === 'string' ? data['message'] : '',
        user: null,
      };
    }
    throw error;
  }
}

// ─────────────────────────────────────────────────────────────────
// User Profile API
// ─────────────────────────────────────────────────────────────────

export async function updateUserProfile(request: UpdateUserProfileRequestDTO) {
  const functionCode = store.getState().global.functionCodes?.users ?? '';
  const { data: response } = await ymsAdminClient.put<UpdateUserProfileResponseDTO>(
    `${Endpoints.UpdateProfile}${functionCode}`,
    request
  );
  return response;
}

export async function UpdateUserLogAsync(request: UserLogRequest) {
  // if (_connectivity.NetworkAccess == NetworkAccess.Internet) {
  const functionCode = store.getState().global.functionCodes?.user_logs ?? '';
  // Xamarin sends an array of UserLogRequestDTOs with field name "plant" (not "plant_id")
  // and no "user" field. Match exactly to avoid backend rejection.
  const dto = [
    {
      status: request.status,
      activity_date_time: request.activity_date_time,
      session_id: request.session_id,
      plant: request.plant_id ?? '',
    },
  ];
  const response = await ymsAdminClient.post<UserLogResponse>(
    `${Endpoints.UserLogs}${functionCode}`,
    dto
  );
  return response;
  // }
  // else {
  //   storeUserLogOffline(request);
  // }
}

// function storeUserLogOffline(request: UserLogRequest): void {
//   const existingLogs = getOfflineUserLogs();
//   existingLogs.push({
//     Status: request.Status,
//     ActivityDateTime: request.ActivityDateTime,
//     SessionId: request.SessionId,
//     PlantId: request.PlantId ?? '',
//   });
//   storeOfflineUserLogs(existingLogs);
// }

// function getOfflineUserLogs(): UserLogRequest[] {
//   try {
//     const raw = localStorage.getItem(STORAGE_KEYS.offlineUserLogStatusList);
//     if (!raw) return [];
//     const parsed: unknown = JSON.parse(raw);
//     return Array.isArray(parsed) ? (parsed as UserLogRequest[]) : [];
//   } catch {
//     return [];
//   }
// }

// function storeOfflineUserLogs(userLogs: UserLogRequest[]): void {
//   localStorage.setItem(STORAGE_KEYS.offlineUserLogStatusList, JSON.stringify(userLogs));
// }

export async function refreshTokenAsync(refreshToken: string) {
  const functionCode = store.getState().global.functionCodes?.refresh_token ?? '';
  const response = await ymsAdminClient.post<RefreshTokenResponse>(
    `${Endpoints.RefreshTokenAPI}${functionCode}`,
    { refresh_token: refreshToken }
  );

  const refreshtoken = String(response.headers['access-token'] ?? '');
  return { refresh_token: refreshtoken };
}

export function mapUpdateTaskRes(raw: UpdateTaskResponse): UpdateTaskFinalResponse {
  const detail = raw.payload.Body.Update_task_details[0];
  const taskDetails = detail?.Task_details;

  return {
    payload: {
      TransmissionHeader: {
        TransmissionDateTime: raw.payload.TransmissionHeader.TransmissionDateTime,
        OriginatingSystem: raw.payload.TransmissionHeader.OriginatingSystem,
        TransmissionUser: raw.payload.TransmissionHeader.TransmissionUser,
        RoutingCode: raw.payload.TransmissionHeader.RoutingCode,
        MessageType: raw.payload.TransmissionHeader.MessageType,
        MessageId: raw.payload.TransmissionHeader.MessageId,
      },
      Body: {
        Status: raw.payload.Body.Status,
        StatusMessage: raw.payload.Body.Status_message,
        StatusCode: raw.payload.Body.Status_code,
        UpdateTaskDetails: [
          {
            Status: detail?.Status ?? '',
            StatusMessage: detail?.Status_message ?? '',
            StatusCode: detail?.Status_code ?? '',
            TaskDetails: {
              ActionCode: taskDetails?.Action_code ?? '',
              OrgId: taskDetails?.Org_id ?? '',
              TaskId: taskDetails?.Task_id ?? '',
              TaskType: taskDetails?.Task_type ?? '',
              Status: taskDetails?.Status ?? '',
              AssignedUserId: taskDetails?.Assigned_user_id ?? '',
              CreatedSource: taskDetails?.Created_source ?? '',
              CreatedDatetime: taskDetails?.Created_datetime ?? '',
              LastUpdatedDatetime: taskDetails?.Last_updated_datetime ?? '',
              LastUpdatedSource: taskDetails?.Last_updated_source ?? '',
              Priority: taskDetails?.Priority ?? '',
              YardId: taskDetails?.Yard_id ?? '',
              TaskStartDatetime: taskDetails?.Task_start_datetime ?? '',
              TaskEndDatetime: taskDetails?.Task_end_datetime ?? '',
              TaskReleaseDatetime: taskDetails?.Task_release_datetime ?? '',
              TaskComments: taskDetails?.Task_comments ?? '',
              TrailerId: taskDetails?.Trailer_id ?? '',
              TrailerName: taskDetails?.Trailer_name ?? '',
              TrailerComments: taskDetails?.Trailer_comments ?? '',
              CarrierCompanyName: taskDetails?.Carrier_company_name ?? '',
              CarrierName: taskDetails?.Carrier_name ?? '',
              AuditLocationType: taskDetails?.Audit_location_type ?? '',
              AuditLocationZone: taskDetails?.Audit_location_zone ?? '',
              AuditLocationName: taskDetails?.Audit_location_name ?? '',
              MoveSourceType: taskDetails?.Move_src_type ?? '',
              MoveSourceZone: taskDetails?.Move_src_zone ?? '',
              MoveSourceName: taskDetails?.Move_src_name ?? '',
              MoveDestinationType: taskDetails?.Move_dst_type ?? '',
              MoveDestinationZone: taskDetails?.Move_dst_zone ?? '',
              MoveDestinationName: taskDetails?.Move_dst_name ?? '',
              displayCreatedDatetime: '',
              displayLastUpdatedDatetime: '',
              ExceptionCode: taskDetails?.Exception_code ?? '',
              TaskAssignedTime: taskDetails?.Task_assigned_time ?? '',
              TaskDescription: '',
            },
            OrgId: detail?.Org_id ?? '',
            TaskId: detail?.Task_id ?? '',
            TaskType: detail?.Task_type ?? '',
            UserId: detail?.User_id ?? '',
            ActionType: detail?.Action_type ?? '',
            ActionDatetime: detail?.Action_datetime ?? '',
            TrailerName: detail?.Trailer_name ?? '',
            CarrierName: detail?.Carrier_name ?? '',
            exceptionCode: detail?.Exception_code ?? '',
            TaskComments: detail?.Task_comments ?? '',
            TrailerComments: detail?.Trailer_comments ?? '',
            TaskStartedDateTime: detail?.TaskStartedDateTime ?? '',
            selectedIndex: detail?.SelectedIndex ?? 0,
            currentLoggedInUser: detail?.CurrentLoggedInUser ?? '',
            AlternateTrailerValid: detail?.AlternateTrailerValid ?? '',
            AlternateCarrier: detail?.AlternateCarrier ?? '',
            AlternateTrailerName: detail?.AlternateTrailerName ?? '',
            AlternateDestinationZone: detail?.AlternateDestinationZone ?? '',
            AlternateDestinationName: detail?.AlternateDestinationName ?? '',
            OverrideReasonCode: detail?.OverrideReasonCode ?? '',
            TaskCommentOverrideReasonCode: detail?.TaskCommentOverrideReasonCode ?? '',
          },
        ],
      },
    },
  };
}
